import React, { useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Timer,
  BarChart3,
  Lock,
  Lightbulb,
  Sparkles,
  Code,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { getTechIcon } from "../data/techIcons";
import BreadcrumbSchema from "../components/BreadcrumbSchema";
import IdeaValidatorModal from "../components/IdeaValidator/IdeaValidatorModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

function useFaqSchema(faqs, path) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-schema", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqs, path]);
}

export default function ServicePage({ service }) {
  useFaqSchema(service.faqs, service.path);

  const HeroVisual = service.heroVisual;
  const DeliveryVisual = service.deliverySection?.visual;
  const [ideaValidatorOpen, setIdeaValidatorOpen] = useState(false);

  return (
    <>
      <Seo
        title={service.title}
        description={service.metaDescription}
        path={service.path}
        image={service.ogImage || service.coverImage}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-[#00AEEF]/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">{service.category}</p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 whitespace-pre-line" data-testid="service-page-heading">
                {service.heading}
              </h1>
              <p className="text-lg text-neutral-300 max-w-xl leading-relaxed mb-10">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="service-cta-primary">
                  <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                    Start Your Project <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/case-studies" data-testid="service-cta-cases">
                  <Button variant="outline" className="border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] px-8 py-6 text-base rounded-md transition-all duration-300">
                    View Case Studies <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            {HeroVisual && <HeroVisual />}
          </div>
        </div>
      </section>

      {/* Trusted By Logos + Clutch */}
      <section className="py-10 bg-[#0A0A0A] border-b border-white/5" data-testid="service-trusted-by">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <p className="text-center text-xs uppercase tracking-widest text-neutral-400 mb-6">Trusted by Industry Leaders</p>
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 opacity-50 hover:opacity-70 transition-opacity">
              {[
                { name: "Google", url: "https://www.maxiomtech.com/images/logos/logo-3-black.svg" },
                { name: "BMW", url: "https://www.maxiomtech.com/images/logos/Mask-Group-6.png" },
                { name: "Exelis", url: "https://www.maxiomtech.com/images/logos/exelis-3-LOGO.png" },
                { name: "Telus", url: "https://www.maxiomtech.com/images/logos/Mask-Group-5.png" },
              ].map((logo) => (
                <img
                  key={logo.name}
                  src={logo.url}
                  alt={`${logo.name} logo`}
                  className="h-7 md:h-9 w-auto brightness-0 invert object-contain"
                  loading="lazy"
                />
              ))}
            </div>
            <a
              href="https://clutch.co/profile/maxiom-technology"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-gray-200 hover:border-[#00AEEF]/60 hover:bg-[#00AEEF]/10 transition-colors"
              data-testid="service-clutch-badge"
            >
              <span className="flex h-5 w-20 items-center justify-center rounded bg-transparent text-[11px] font-bold text-[#F59E0B] tracking-tight leading-none">
                ★★★★★
              </span>
              <span className="font-semibold text-white">Top-rated on Clutch</span>
              <span className="text-gray-400 hidden sm:inline">
                Champion &amp; Global Leader
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Free App Idea Assessment Teaser (same CTA across service pages) */}
      <section
        data-testid="idea-validator-teaser"
        className="py-20 md:py-32 bg-[#0A0A0A] border-y border-white/5"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-10">
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00AEEF] to-[#06B6D4]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/10 via-transparent to-transparent pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row lg:items-start gap-10">
              <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#00AEEF]/10 border border-[#00AEEF]/30 rounded-full px-4 py-[6px] text-[12px] font-medium text-[#06B6D4] tracking-[0.5px] mb-6">
                Free App Idea Assessment
              </div>

              <h2 className="font-bold text-[clamp(28px,5vw,44px)] leading-[1.1] mb-5 text-white">
                How Good is Your
                <br />
                <span className="bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] bg-clip-text text-transparent">
                  App Idea?
                </span>
              </h2>

              <p className="text-neutral-400 text-[15px] leading-relaxed mb-10">
                Answer 8 questions. A senior engineer reads the result. You get a viability read and a next step, not a drip sequence. We&apos;ve been shipping since 2002.
              </p>

              <div className="flex flex-col sm:flex-row gap-5 flex-wrap mb-10">
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <Timer className="w-5 h-5 text-[#00AEEF]" />
                  <span>Takes about 2 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                  <span>Free personalized report</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <Lock className="w-5 h-5 text-[#00AEEF]" />
                  <span>No spam, ever</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={() => setIdeaValidatorOpen(true)}
                className="bg-gradient-to-r from-[#00AEEF] to-[#2563eb] hover:from-[#00AEEF] hover:to-[#1d4ed8] text-white font-bold px-10 py-5 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)]"
              >
                Start Your Free Assessment <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              </div>

              {/* Right graphic (subtle fade-in) */}
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="hidden lg:block flex-1 min-w-[220px] relative h-full"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-l from-[#00AEEF]/20 via-transparent to-transparent blur-2xl" />
                <div className="absolute -right-10 top-10 w-44 h-44 rounded-full bg-[#06B6D4]/10 blur-2xl" />
                <div className="absolute right-5 bottom-0 w-56 h-56 rounded-full border border-[#00AEEF]/20 blur-[0px] opacity-40" />

                <div className="relative z-10 mt-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/10 border border-[#00AEEF]/30 flex items-center justify-center mb-5">
                    <Lightbulb className="w-6 h-6 text-[#00AEEF]" />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 border border-[#00AEEF]/30 flex items-center justify-center opacity-80">
                      <Code className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-[#06B6D4]/10 border border-[#06B6D4]/30 flex items-center justify-center opacity-80">
                      <Sparkles className="w-5 h-5 text-[#06B6D4]" />
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="h-[2px] w-24 bg-gradient-to-r from-[#00AEEF] to-transparent opacity-70 mb-3" />
                    <div className="h-[2px] w-32 bg-gradient-to-r from-[#06B6D4] to-transparent opacity-50" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="service-features">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Capabilities</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{service.featuresTitle}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#00AEEF]/30 transition-all duration-300"
                data-testid={`service-feature-${i}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center mb-6">
                  <f.icon className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery visibility: HTML mockup paired with copy (per app-mockup-visuals rule) */}
      {service.deliverySection && (
        <section
          className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5"
          data-testid="service-delivery"
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">
                  {service.deliverySection.label}
                </p>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  {service.deliverySection.heading}
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                  {service.deliverySection.description}
                </p>
                <ul className="space-y-4">
                  {service.deliverySection.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3"
                      data-testid={`delivery-bullet-${i}`}
                    >
                      <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm leading-relaxed">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              {DeliveryVisual && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <DeliveryVisual />
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sub-services (for grouped offerings like Mobile) */}
      {service.subServices && service.subServices.length > 0 && (
        <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="service-sub-services">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-10">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">Platform-Specific Services</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Deep Expertise Across Mobile Platforms</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {service.subServices.map((sub, i) => (
                <motion.div
                  key={sub.path}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                >
                  <Link
                    to={sub.path}
                    className="block bg-[#121212] border border-white/5 rounded-xl p-5 hover:border-[#00AEEF]/40 transition-all duration-300 h-full"
                  >
                    <h3 className="text-sm font-semibold text-white mb-2">{sub.title}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed">{sub.desc}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#00AEEF] font-medium">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tech Stack */}
      <section className="py-20 md:py-32 bg-[#0f0f0f]" data-testid="service-tech-stack">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Technology</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Tech Stack</h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {service.techStack.map((tech, i) => {
              const iconUrl = getTechIcon(tech);
              return (
                <motion.div
                  key={tech}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.05}
                  className="bg-[#121212] border border-white/5 rounded-lg px-4 py-5 flex flex-col items-center gap-3 text-center hover:border-[#00AEEF]/30 hover:bg-[#00AEEF]/5 transition-all duration-300 group"
                  data-testid={`tech-${i}`}
                >
                  {iconUrl ? (
                    <img
                      src={iconUrl}
                      alt={`${tech} logo`}
                      className="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center">
                      <span className="text-[#00AEEF] font-bold text-lg">{tech.charAt(0)}</span>
                    </div>
                  )}
                  <span className="text-sm text-gray-300 group-hover:text-[#00AEEF] transition-colors">{tech}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="service-benefits">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Why Choose Us</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">{service.benefitsTitle}</h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-4">{service.benefitsDescription}</p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}>
              <ul className="space-y-5">
                {service.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-4" data-testid={`benefit-${i}`}>
                    <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium mb-1">{b.title}</div>
                      <div className="text-gray-400 text-sm">{b.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO Content - Industry Solutions */}
      {service.seoContent && (
        <section className="py-20 md:py-32 bg-[#0A0A0A] border-t border-white/5" data-testid="service-seo-content">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">{service.seoContent.label}</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{service.seoContent.heading}</h2>
            </motion.div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <p className="text-gray-400 leading-relaxed text-base mb-6">{service.seoContent.body1}</p>
                <p className="text-gray-400 leading-relaxed text-base">{service.seoContent.body2}</p>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.seoContent.industries.map((ind, i) => (
                    <div key={i} className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#00AEEF]/20 transition-all" data-testid={`industry-${i}`}>
                      <h4 className="text-white font-medium mb-2">{ind.name}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{ind.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Related services and landing pages */}
      {service.relatedServices && service.relatedServices.length > 0 && (
        <section className="py-12 md:py-16 bg-[#0A0A0A]" data-testid="service-related-links">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-6">
              <p className="text-[#00AEEF] text-xs uppercase tracking-widest font-medium mb-2">Related Solutions</p>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">
                Explore related services and specialized landing pages.
              </h3>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {service.relatedServices.map((rel, i) => (
                <motion.div
                  key={rel.path}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                >
                  <Link
                    to={rel.path}
                    className="block bg-[#121212] border border-white/5 rounded-xl px-4 py-4 hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5 transition-all text-sm text-gray-200"
                  >
                    {rel.name}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="py-20 md:py-32 bg-[#0f0f0f]" data-testid="service-faq">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="lg:col-span-4"
              >
                <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">FAQ</p>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  Frequently Asked Questions
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  Have more questions? We'd love to hear from you. Schedule a call and our team will walk you through everything.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 text-[#00AEEF] font-medium mt-6 hover:gap-3 transition-all" data-testid="faq-contact-link">
                  Get in Touch <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={1}
                className="lg:col-span-8"
              >
                <Accordion type="single" collapsible className="space-y-3">
                  {service.faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="bg-[#121212] border border-white/5 rounded-xl px-6 data-[state=open]:border-[#00AEEF]/30 transition-colors"
                      data-testid={`faq-item-${i}`}
                    >
                      <AccordionTrigger className="text-white text-left font-medium py-5 hover:text-[#00AEEF] hover:no-underline [&[data-state=open]]:text-[#00AEEF]">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-400 text-sm leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="service-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Ready to Start Your {service.shortTitle} Project?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Let's discuss how Maxiom Apps can bring your vision to life with our expert team.
            </p>
            <Link to="/contact" data-testid="service-bottom-cta">
              <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                Schedule a Call <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <IdeaValidatorModal
        open={ideaValidatorOpen}
        onOpenChange={setIdeaValidatorOpen}
        initialFirstName=""
        initialStep={1}
      />
    </>
  );
}
